import { SourceGlyph, cx } from '@flexbyt/widget-preview';
import { createFileRoute } from '@tanstack/react-router';
import { PageHero } from '../components/ui/hero.js';
import { IntegrationsScene } from '../components/mascot/scenes.js';
import { SiteLink, linkClass } from '../components/ui/button.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import {
    CHROME_EXTENSION_URL,
    DIRECT_INTEGRATIONS,
    EXTENSION_INTEGRATIONS,
    integrationPath,
    type IntegrationContent,
} from '../content/integrations.js';
import { breadcrumbSchema, jsonLd, seo } from '../lib/seo.js';

/**
 * Where reviews can come from.
 *
 * ===========================================================================
 * Two lists, and it has to be two lists
 * ===========================================================================
 * One grid of nine logos with a small badge on four of them is the version that
 * flattens the distinction the brief is firm about: a badge reads as a
 * footnote, and the visitor takes away "ReviewsJet imports from these nine
 * places, somehow".
 *
 * They are different promises. Five are fetched server-side from a link you
 * paste. Four cannot be read from a server at all and are collected by the
 * Chrome extension in your own browser. Somebody who expects the first and gets
 * the second finds out after signing up, which is the worst possible moment.
 *
 * So: two headings, two explanations, and each group says what it needs before
 * anybody clicks into it.
 */
export const Route = createFileRoute('/integrations/')({
  head: () => {
    const { meta, links } = seo({
      title: 'Review integrations',
      description:
        'Import reviews from Google, Trustpilot, Yelp, Tripadvisor, Airbnb, the App Store and Google Play by pasting a link — plus Etsy, Amazon, AliExpress and TourRadar through the ReviewsJet Chrome extension.',
      path: '/integrations',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Integrations', path: '/integrations' },
            ])
          ),
        },
      ],
    };
  },
  component: Integrations,
});

function Integrations() {
  return (
    <>
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Integrations', path: '/integrations' },
        ]}
        art={<IntegrationsScene />}
      >
        <Measure>
          <Eyebrow>Integrations</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            Wherever your reviews <span className="whitespace-nowrap"><Mark>already are</Mark>.</span>
          </Heading>
          <Text size="lead" className="mt-5">
            Nine platforms, in two groups — because they do not all work the
            same way, and knowing which is which before you start matters more
            than a tidy grid.
          </Text>
        </Measure>
      </PageHero>

      <Section aria-labelledby="direct-heading">
        <Measure>
          <Heading level={2} id="direct-heading">
            Paste a link, get the reviews.
          </Heading>
          <Text className="mt-4 max-w-prose">
            Give ReviewsJet the address of your public listing. It fetches what
            your customers wrote, keeps checking for new ones, and everything
            lands in one library where you decide what goes on your site.
          </Text>
        </Measure>

        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {DIRECT_INTEGRATIONS.map((entry) => (
            <SourceCard key={entry.slug} entry={entry} />
          ))}
        </ul>
      </Section>

      <Section tone="raised" aria-labelledby="extension-heading">
        <Measure>
          <Heading level={2} id="extension-heading">
            Collected by the Chrome extension.
          </Heading>
          <Text className="mt-4 max-w-prose">
            These marketplaces cannot be read from a server, so there is no link
            to paste. The free ReviewsJet extension picks the reviews up in your
            own browser, on your own page, while you are signed in to them —
            then they join everything else in your library.
          </Text>
          <Text size="small" tone="subtle" className="mt-3">
            Extension sources are collected when you run them. The five above
            refresh on their own.
          </Text>
        </Measure>

        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {EXTENSION_INTEGRATIONS.map((entry) => (
            <SourceCard key={entry.slug} entry={entry} />
          ))}
        </ul>

        <p className="mt-8">
          <a
            href={CHROME_EXTENSION_URL}
            target="_blank"
            rel="noreferrer"
            className={linkClass}
          >
            Get the ReviewsJet Chrome extension
          </a>{' '}
          <span className="text-body-sm text-ink-500">
            — free, and only needed for these four.
          </span>
        </p>
      </Section>
    </>
  );
}

function SourceCard({ entry }: { entry: IntegrationContent }) {
  return (
    <li>
      <SiteLink
        to={integrationPath(entry)}
        className={cx(
          'group flex h-full flex-col rounded-xl bg-white p-5 ring-1 ring-ink-150',
          'transition-colors duration-[var(--duration-fast)]',
          'hover:ring-ink-300'
        )}
      >
        <SourceGlyph source={entry.id} className="size-7" />
        <span className="mt-4 block text-title text-ink-950">{entry.name}</span>
        <span className="mt-1.5 block flex-1 text-body-sm text-ink-600">
          {entry.audience}
        </span>
        <span className="mt-4 inline-flex items-center gap-1.5 text-caption font-medium text-ink-700 group-hover:text-ink-950">
          {entry.name} reviews
          <svg
            viewBox="0 0 20 20"
            aria-hidden
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-3.5 transition-transform duration-[var(--duration-fast)] group-hover:translate-x-0.5"
          >
            <path d="m7.8 4.6 5.4 5.4-5.4 5.4" />
          </svg>
        </span>
      </SiteLink>
    </li>
  );
}
